/**
 * Device marker mapping for the 2D plan + 3D viewer: one distinctive shape
 * and color per device kind (task contract: detector circle, sounder
 * triangle, strobe square, pull station pentagon, FACP star).
 *
 * Pure, deterministic helpers — no IO, no randomness.
 */

export const MARKER_SHAPES = [
  "circle",
  "triangle",
  "square",
  "pentagon",
  "star",
  "diamond",
] as const;
export type MarkerShape = (typeof MARKER_SHAPES)[number];

/** Device kinds the fire-alarm catalog serializes (snake_case). */
const SHAPE_BY_KIND: Record<string, MarkerShape> = {
  smoke_detector: "circle",
  heat_detector: "circle",
  sounder: "triangle",
  strobe: "square",
  sounder_strobe: "square",
  pull_station: "pentagon",
  fire_alarm_control_panel: "star",
  annunciator: "star",
  monitor_module: "diamond",
  control_module: "diamond",
  isolator_module: "diamond",
  // Electrical (ENG-14935)
  receptacle: "square",
  luminaire: "circle",
  switch: "triangle",
  panelboard: "star",
};

const COLOR_BY_KIND: Record<string, string> = {
  smoke_detector: "#61afef",
  heat_detector: "#d19a66",
  sounder: "#98c379",
  strobe: "#e5c07b",
  sounder_strobe: "#e0a23c",
  pull_station: "#e06c75",
  fire_alarm_control_panel: "#c678dd",
  annunciator: "#a98bd3",
  monitor_module: "#8e8eb8",
  control_module: "#8e8eb8",
  isolator_module: "#8e8eb8",
  // Electrical (ENG-14935)
  receptacle: "#56b6c2",
  luminaire: "#e5c07b",
  switch: "#98c379",
  panelboard: "#c678dd",
};

const FALLBACK_COLOR = "#aab2c0";

export function markerShape(kind: string): MarkerShape {
  return SHAPE_BY_KIND[kind] ?? "diamond";
}

export function markerColor(kind: string): string {
  return COLOR_BY_KIND[kind] ?? FALLBACK_COLOR;
}

/** Human label for a device kind, e.g. "smoke detector". */
export function kindLabel(kind: string): string {
  return kind.replace(/_/g, " ");
}

/** Number of polygon vertices per non-circle shape (star = 10: 5 points + 5 valleys). */
const POLYGON_VERTICES: Record<Exclude<MarkerShape, "circle">, number> = {
  triangle: 3,
  square: 4,
  pentagon: 5,
  star: 10,
  diamond: 4,
};

/**
 * SVG polygon vertices for a marker shape centred on (cx, cy) with outer
 * radius `r` (already in the plan view's flipped-y coordinate space).
 * Circles render as `<circle>`, so this returns null for them.
 */
export function markerPolygonPoints(
  shape: MarkerShape,
  cx: number,
  cy: number,
  r: number,
): string | null {
  if (shape === "circle") return null;
  const n = POLYGON_VERTICES[shape];
  const points: string[] = [];
  for (let i = 0; i < n; i += 1) {
    // Start pointing up (SVG y grows downward, so up is -y).
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / n;
    let radius = r;
    if (shape === "star" && i % 2 === 1) radius = r * 0.45;
    if (shape === "diamond" && i % 2 === 1) radius = r * 0.7;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    points.push(`${round(x)},${round(y)}`);
  }
  return points.join(" ");
}

function round(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/** Deterministic legend rows for the kinds present in a device list. */
export function markerLegend(kinds: readonly string[]): { kind: string; shape: MarkerShape; color: string }[] {
  const unique = [...new Set(kinds)].sort();
  return unique.map((kind) => ({
    kind,
    shape: markerShape(kind),
    color: markerColor(kind),
  }));
}
